import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API_URL, post } from "../api";
import { navigateForOnboardingStep, pathForNextStep } from "../utils/onboardingNextStep";
import { useAuth } from "../context/AuthContext";

export default function ResumeOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) return;

      // If the browser has a stale/expired JWT, the app-level profile query can
      // redirect to /login before we get a chance to exchange this link.
      // Clear it up-front; the exchange will store a fresh token.
      localStorage.removeItem("token");

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const res = await post(
          `${API_URL}/onboarding/users/onboarding/resume-onboarding-link/exchange`,
          { token }
        );

        const payload = res?.data as
          | { token?: string; nextStep?: string }
          | undefined;

        if (!payload?.token) {
          throw new Error("Invalid response from server");
        }

        localStorage.setItem("token", payload.token);

        // Some onboarding steps live under /home/* and are guarded by
        // `withFullOnboarding`, which requires the user profile to be loaded.
        // Await refetch to avoid an immediate redirect back to /login.
        const result = await refetch();
        const user =
          result &&
          "data" in result &&
          result.data?.data &&
          typeof result.data.data === "object"
            ? (result.data.data as any)
            : null;

        const targetPath = pathForNextStep(payload.nextStep);
        if (targetPath?.startsWith("/home") && !user) {
          throw new Error("Signed in, but could not load your profile. Please try again.");
        }

        navigateForOnboardingStep(navigate, payload.nextStep, user);
      } catch (e: any) {
        if (cancelled) return;
        const message =
          typeof e?.message === "string" && e.message.trim().length > 0
            ? e.message
            : "Unable to resume onboarding. Please try again.";
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [token, navigate, refetch]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-6">
          <h1 className="text-xl font-semibold text-gray-900">Resume onboarding</h1>
          <p className="mt-2 text-sm text-gray-600">
            This link is missing a token. Please open the link from your email
            again.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <Link className="text-visaro-orange font-semibold" to="/login">
              Go to login
            </Link>
            <span className="text-gray-300">|</span>
            <Link className="text-visaro-orange font-semibold" to="/sign-up">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-6">
        <h1 className="text-xl font-semibold text-gray-900">Resume onboarding</h1>
        {isLoading ? (
          <p className="mt-2 text-sm text-gray-600">
            Validating your link and signing you in…
          </p>
        ) : errorMessage ? (
          <>
            <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
            <div className="mt-4 flex items-center gap-3">
              <Link className="text-visaro-orange font-semibold" to="/login">
                Go to login
              </Link>
              <span className="text-gray-300">|</span>
              <Link className="text-visaro-orange font-semibold" to="/sign-up">
                Sign up
              </Link>
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-600">Redirecting…</p>
        )}
      </div>
    </div>
  );
}
